// Professional A4 portrait invoice template — Java SPA branding.
// Used both for client-side PDF rendering AND on-screen preview.

import { QRCodeSVG } from "qrcode.react";
import { formatSAR, formatDate } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_spa-pos-system/artifacts/zpy718ez_Logo%20Java%20Spa.png";
const SPA_INFO = {
  name: "Java SPA",
  tagline: "Riyadh · Luxury Wellness",
  address: "Prince Ahmed bin Abdulaziz Rd, King Fahd, Riyadh 11421",
  phone: "+966 55 222 1465",
};

const styles = {
  page: {
    width: "210mm",
    minHeight: "297mm",
    padding: "16mm 14mm",
    boxSizing: "border-box",
    background: "#ffffff",
    color: "#1a1a1a",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    lineHeight: 1.45,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 14,
    borderBottom: "2px solid #D4AF37",
    marginBottom: 18,
  },
  brand: { display: "flex", alignItems: "center", gap: 14 },
  brandName: { fontSize: 26, fontWeight: 800, color: "#1a1a1a", letterSpacing: 1 },
  brandTag: { fontSize: 10, color: "#6DBE45", letterSpacing: 3, textTransform: "uppercase", marginTop: 2 },
  brandSub: { fontSize: 10, color: "#666", marginTop: 4 },
  invMeta: { textAlign: "right" },
  label: { fontSize: 9, letterSpacing: 2, color: "#999", textTransform: "uppercase" },
  invNo: { fontSize: 16, fontWeight: 700, fontFamily: "monospace", marginTop: 2 },
  title: { textAlign: "center", margin: "10px 0 16px" },
  titleSmall: { fontSize: 10, letterSpacing: 4, color: "#B8901E", textTransform: "uppercase" },
  titleBig: { fontSize: 28, fontWeight: 800, marginTop: 6, color: "#1a1a1a" },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px", marginBottom: 18 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12, border: "1px solid #e3decf", borderRadius: 6, overflow: "hidden" },
  th: { background: "#FFF8E1", color: "#B8901E", padding: "10px 12px", fontSize: 10, textTransform: "uppercase", letterSpacing: 1.2, textAlign: "left" },
  thRight: { background: "#FFF8E1", color: "#B8901E", padding: "10px 12px", fontSize: 10, textTransform: "uppercase", letterSpacing: 1.2, textAlign: "right" },
  td: { padding: "10px 12px", borderTop: "1px solid #f0e9c8", verticalAlign: "top" },
  tdRight: { padding: "10px 12px", borderTop: "1px solid #f0e9c8", verticalAlign: "top", textAlign: "right" },
  totalsWrap: { display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 24, alignItems: "start", marginTop: 18 },
  totalsBox: { background: "#FAF7EE", border: "1px solid #e3decf", borderRadius: 8, padding: 14 },
  totalsRow: { display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 12 },
  totalsGrand: { display: "flex", justifyContent: "space-between", padding: "10px 12px", marginTop: 6, background: "#D4AF37", color: "#fff", borderRadius: 6, fontSize: 16, fontWeight: 800 },
  qrBox: { textAlign: "center", padding: 10, border: "1px dashed #D4AF37", borderRadius: 8, background: "#fff" },
  qrLabel: { fontSize: 9, letterSpacing: 1.5, color: "#999", textTransform: "uppercase", marginTop: 6 },
  thanks: { textAlign: "center", marginTop: 20, color: "#6DBE45", fontStyle: "italic" },
  footer: { textAlign: "center", paddingTop: 12, borderTop: "1px solid #e8e4d4", marginTop: 20, fontSize: 9, color: "#999" },
};

const InvoiceTemplate = function InvoiceTemplate({ tx, invoiceUrl, paymentMethod = "Tunai", customerName = "" }) {
  const { t, lang } = useLang();
  const rtl = lang === "ar";
  const invoiceNo = (tx?.id || "").slice(0, 8).toUpperCase();
  const subtotal = tx?.subtotal || 0;
  const discount = tx?.discount_amount || 0;
  const total = tx?.total || 0;
  const items = tx?.items || [];
  const itemName = (it) =>
    (rtl && it.service_name_ar) ||
    (lang === "en" && it.service_name_en) ||
    it.service_name_id;

  return (
    <div style={styles.page} dir={rtl ? "rtl" : "ltr"}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.brand}>
          <img src={LOGO_URL} alt="Java SPA" style={{ height: 60, width: 60, objectFit: "contain" }} crossOrigin="anonymous" />
          <div>
            <div style={styles.brandName}>{SPA_INFO.name}</div>
            <div style={styles.brandTag}>{SPA_INFO.tagline}</div>
            <div style={styles.brandSub}>{SPA_INFO.address}</div>
            <div style={styles.brandSub}>{SPA_INFO.phone}</div>
          </div>
        </div>
        <div style={{ ...styles.invMeta, textAlign: rtl ? "left" : "right" }}>
          <div style={styles.label}>{t("receipt.invoiceNo")}</div>
          <div style={styles.invNo}>#{invoiceNo}</div>
          <div style={{ ...styles.label, marginTop: 8 }}>{t("receipt.issued")}</div>
          <div style={{ fontSize: 11, marginTop: 2 }}>{formatDate(tx?.created_at)}</div>
        </div>
      </div>

      {/* TITLE */}
      <div style={styles.title}>
        <div style={styles.titleSmall}>— {t("kasir.receipt")} —</div>
        <div style={styles.titleBig}>{t("receipt.title")}</div>
      </div>

      {/* CUSTOMER + KASIR */}
      <div style={styles.twoCol}>
        <div>
          <div style={styles.label}>{t("receipt.customer")}</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{customerName || "—"}</div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{tx?.customer_phone || "-"}</div>
        </div>
        <div style={{ textAlign: rtl ? "left" : "right" }}>
          <div style={styles.label}>{t("receipt.cashier")}</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{tx?.cashier_name || "—"}</div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{formatDate(tx?.created_at)}</div>
        </div>
      </div>

      {/* ITEMS TABLE */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: 30, textAlign: rtl ? "right" : "left" }}>#</th>
            <th style={{ ...styles.th, textAlign: rtl ? "right" : "left" }}>{t("receipt.items")}</th>
            <th style={{ ...styles.thRight, width: 50, textAlign: rtl ? "left" : "right" }}>{t("receipt.qty")}</th>
            <th style={{ ...styles.thRight, width: 110, textAlign: rtl ? "left" : "right" }}>{t("receipt.price")}</th>
            <th style={{ ...styles.thRight, width: 120, textAlign: rtl ? "left" : "right" }}>{t("kasir.subtotal")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={`${it.service_id}-${it.therapist_id}-${i}`}>
              <td style={styles.td}>{i + 1}</td>
              <td style={styles.td}>
                <div style={{ fontWeight: 600 }}>{itemName(it)}</div>
                <div style={{ fontSize: 10, color: "#777", marginTop: 2 }}>
                  {t("receipt.therapist")}: {it.therapist_name}
                </div>
              </td>
              <td style={{ ...styles.tdRight, textAlign: rtl ? "left" : "right" }}>1</td>
              <td style={{ ...styles.tdRight, textAlign: rtl ? "left" : "right" }}>{formatSAR(it.price)}</td>
              <td style={{ ...styles.tdRight, textAlign: rtl ? "left" : "right" }}>{formatSAR(it.price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* TOTALS + QR */}
      <div style={styles.totalsWrap}>
        {/* Left: Payment + QR */}
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <div style={styles.label}>{t("receipt.paymentMethod")}</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4, color: "#1a1a1a" }}>{paymentMethod}</div>
          </div>
          {invoiceUrl && (
            <div style={styles.qrBox}>
              <QRCodeSVG value={invoiceUrl} size={110} level="M" includeMargin={false} />
              <div style={styles.qrLabel}>{t("receipt.scanInvoice")}</div>
            </div>
          )}
        </div>

        {/* Right: Totals */}
        <div style={styles.totalsBox}>
          <div style={styles.totalsRow}>
            <span style={{ color: "#666" }}>{t("kasir.subtotal")}</span>
            <span style={{ fontWeight: 600 }}>{formatSAR(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div style={{ ...styles.totalsRow, color: "#6DBE45" }}>
              <span>{t("kasir.discount")}</span>
              <span>- {formatSAR(discount)}</span>
            </div>
          )}
          <div style={styles.totalsGrand}>
            <span>{t("receipt.grandTotal")}</span>
            <span>{formatSAR(total)}</span>
          </div>
        </div>
      </div>

      {/* THANK YOU */}
      <div style={styles.thanks}>
        {t("receipt.thanks")}
        {lang !== "ar" && <> · شكرًا لزيارتكم</>}
      </div>

      {/* FOOTER */}
      <div style={styles.footer}>
        {SPA_INFO.name} · {SPA_INFO.address} · {SPA_INFO.phone}
        <div style={{ marginTop: 3 }}>{t("receipt.generated")} {new Date().toLocaleString()}</div>
      </div>
    </div>
  );
};

export default InvoiceTemplate;
